import https from 'https';
import { computed, useContext } from '@nuxtjs/composition-api';
import { stringify } from 'qs';
import { StrapiMultiResponse, StrapiResponse } from '~/types/strapi/strapi';

type StrapiRequestParams = Record<string, unknown>;

interface UseStrapi<T> {
  find: (params?: StrapiRequestParams) => Promise<StrapiMultiResponse<T>>;
  findOne: (id: string, params?: StrapiRequestParams) => Promise<StrapiResponse<T>>;
  getSingleType: () => Promise<StrapiResponse<T>>;
}

type ContentType = 'collections' | 'hub-page';

export const useStrapi = <T>(contentType: ContentType): UseStrapi<T> => {
  const { $axios, $config, i18n } = useContext();
  const locale = computed(() => `en-${i18n.locale.toUpperCase()}`);

  const client = $axios.create({
    baseURL: `${$config.strapi.baseURL}/api`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `bearer ${$config.strapi.apiKey}`,
    },
    httpsAgent: new https.Agent({ rejectUnauthorized: process.env.NODE_ENV !== 'development' }),
  });

  const find = async (params?: StrapiRequestParams): Promise<StrapiMultiResponse<T>> => {
    const { data } = await client.get<StrapiMultiResponse<T>>(`/${contentType}`, {
      params: {
        ...params,
        populate: 'deep',
        locale: locale.value,
      },
    });
    return data;
  };

  const getSingleType = async (): Promise<StrapiResponse<T>> => {
    try {
      const { data } = await client.get<StrapiResponse<T>>(`/${contentType}`, {
        params: {
          populate: 'deep',
          locale: locale.value,
        },
      });
      return data;
    } catch (error) {
      console.error('Error fetching single type:', error);
      throw error;
    }
  };

  /**
   *
   * We do a find request to Strapi instead of findOne because we need to query based on slug
   * instead of ID. To move to findOne we would need to modify the controller in Strapi
   * and make sure the slug is always unique, even across locales.
   */
  const findOne = async (
    slug: string,
    params?: StrapiRequestParams
  ): Promise<StrapiResponse<T>> => {
    const query = stringify({
      ...params,
      filters: {
        link: {
          $eq: slug,
        },
      },
      populate: 'deep',
      locale: locale.value,
    });
    const { data } = await client.get<StrapiMultiResponse<T>>(`/${contentType}?${query}`, {
      headers: {
        Authorization: `bearer ${$config.strapi.apiKey}`,
      },
    });
    return { data: data.data[0] };
  };

  return { findOne, find, getSingleType };
};
